/**
 * Shared functions for subscribing push notifications
 *
 * @author    Taoran Cai <tcai@wayfair.com>
 * @copyright 2017 Wayfair, LLC - All rights reserved
 */
const SUBSCRIBE_PERMISSION_DENIED_EVENT = 'WebPushSubscribeDenied';
const SUBSCRIBE_PERMISSION_DEFAULT_EVENT = 'WebPushSubscribeDefault';
const ACTION_SUBSCRIBE = 'subscribe';
const SUBSCRIBE_PERMISSION_DENIED = 'denied';
const SUBSCRIBE_PERMISSION_DEFAULT = 'default';
const ERROR_MESSAGE_PREFIX = 'web push notification error: ';

/**
 * Check if current browser supports web push notification
 * @returns true if current browser supports web push notification
 */

const supportWebPush = () =>
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window;

/**
 * Check if current browser supports notifications
 * @returns true if current browser supports notification
 */

const supportNotifications = () => 'Notification' in window;

/**
 * Determines whether web push notification is supported
 * Note: Exclude Opera since web push notification is not stable on it
 * Note: Decouples feature toggle detection, passing that responsibility to the caller
 */

export const supportWebPushSubscription = applicationServerKey => {
  return (
    typeof window !== 'undefined' &&
    // used to be wf.appData here
    applicationServerKey &&
    supportWebPush() &&
    supportNotifications() &&
    // Notification.permission !== SUBSCRIBE_PERMISSION_DENIED &&
    !navigator.userAgent.match(/Opera|OPR\//i)
  );
};

/**
 * Update the subsription data on server
 *
 * @param {string} subscribe or unsubscribe
 * @param {JSON}   subsriptionStr string (endpoint and keys)
 */

const updateSubscriptionOnServer = (action, subscriptionStr) => {
  const subscription = JSON.parse(subscriptionStr);
  const data = {
    action,
    registrationEndpoint: subscription.endpoint,
    authKey: subscription.keys.auth,
    publicKey: subscription.keys.p256dh,
    //   browserName: featureDetect.browserName(),
    //   browserVersion: featureDetect.browserVersion(),
    //   operatingSystem: featureDetect.OS(),
    //  customerId: wf.appData.session.customerID
  };

  return fetch('/a/notification_track/update_subscription', {
    credentials: 'include',
    method: 'post',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
};

/**
 * Subscribe the web push notification
 * @param {string} application server key in format of base 64 string
 */

const subscribe = (applicationServerKeyBase64, client, logger) => {
  if (!applicationServerKeyBase64) {
    return;
  }
  navigator.serviceWorker.ready.then(serviceWorkerRegistration => {
    serviceWorkerRegistration.pushManager
      .subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertBase64ToUint8Array(
          applicationServerKeyBase64
        ),
      })
      .then(subscription => {
        client.recordEvent(ACTION_SUBSCRIBE);
        updateSubscriptionOnServer(
          ACTION_SUBSCRIBE,
          JSON.stringify(subscription)
        );
      })
      .catch(e => {
        const permission = Notification
          ? Notification.permission.toString()
          : '';
        if (permission === SUBSCRIBE_PERMISSION_DENIED) {
          client.recordEvent(SUBSCRIBE_PERMISSION_DENIED_EVENT);
        } else if (permission === SUBSCRIBE_PERMISSION_DEFAULT) {
          client.recordEvent(SUBSCRIBE_PERMISSION_DEFAULT_EVENT);
        } else {
          logger.info(`${ERROR_MESSAGE_PREFIX}${e}`, Notification);
        }
      });
  });
};

/**
 * Convert application server key from format of base 64 string to uint 8 array
 * It's necessary when subscribing a user with PushManager
 * https://developers.google.com/web/fundamentals/engage-and-retain/push-notifications/subscribing-a-user#subscribe_a_user_with_pushmanager
 *
 * @param {string} application server key in format of base 64 string
 */

const convertBase64ToUint8Array = applicationServerKeyBase64 => {
  // Padding characters help satisfy length requirements and carry no meaning
  const padding = '='.repeat((4 - (applicationServerKeyBase64.length % 4)) % 4); // Replace '-' and '_' with '+' and '/' to to make the string URL-safe
  const base64 = (applicationServerKeyBase64 + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/'); // Decodes a string of data which has been encoded using base-64 encoding

  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
};

export default subscribe;
